<script setup lang="ts">
import { AssetType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';
import { resolveMarriedPartnerId } from '@/helpers/resolveMarriedPartnerId';
import { useRequestIncomeExpenseProcess } from '@/helpers/useRequestIncomeExpenseProcess';
import type { ApplicantId, PropertyAsset } from '@/types';
import { useTemplateRefsList } from '@vueuse/core';
import { cloneDeep } from 'lodash-es';
import { ref } from 'vue';
import AssetForm from './modules/Assets/AssetForm.vue';
import { groupApplicantsByEntity } from '@/helpers/builder';
import { toast } from '@/helpers/toast';

const { authorisedUser, loanApp, loanApplicants, saveAndGoNext, goBack } = useRequestIncomeExpenseProcess();

const isSecondaryDeclaration = false;

const getApplicantIndex = (id: string) => {
  return loanApp.value.applicants.findIndex((item) => item.id === id);
};

// Every applicant on the loan is shown (so co-borrowers under the same company/trust entity
// appear, regardless of user type); editing is gated by canEditApplicantAssets.
const validApplicants = loanApp.value.applicants.map((a) => a.id);

const financials = ref(cloneDeep(loanApp.value.financials));

// Editable: the logged-in user's own assets, plus a married/defacto partner's. Every other
// co-borrower is shown read-only — they declare their own via the email request.
// Resolve the partner from marriage relationship rows (either direction), not via a primary
// applicant — company/trust borrowers can have no primary applicant on the loan.
const partnerId = resolveMarriedPartnerId(authorisedUser.id, loanApp.value.applicants);

// An applicant who has already submitted (declared) is locked — view-only for everyone,
// including a partner re-opening the household after the other member submitted.
const hasSubmitted = (applicantId: ApplicantId): boolean =>
  (loanApp.value.declaredApplicantIds ?? []).includes(applicantId);

// A married household is submitted as a unit, so once either member has declared the whole
// household is locked — view-only for the partner logging in after.
const householdSubmitted = hasSubmitted(authorisedUser.id as ApplicantId) || (!!partnerId && hasSubmitted(partnerId));

const canEditApplicantAssets = (applicantId: ApplicantId): boolean => {
  if (loanApp.value.userType === 'Broker') return true;
  if (hasSubmitted(applicantId)) return false;
  if (householdSubmitted) return false;
  if (applicantId === authorisedUser.id) return true;
  if (partnerId && applicantId === partnerId) return true;
  return false;
};

// Entity-first layout: company/trust borrowers become section headers with their members
// nested beneath; applicants in no entity fall into a trailing un-titled section. A member of
// several entities is listed under each, but only their anchor occurrence renders a form.
const entitySections = groupApplicantsByEntity(validApplicants, loanApp.value.borrowerGroups, loanApp.value.applicants);

const editableApplicants = validApplicants.filter((id) => canEditApplicantAssets(id));

// Co-borrowers outside the user's household declare their own via the email request.
const otherApplicantNames = loanApp.value.applicants
  .filter((a) => a.id !== authorisedUser.id && a.id !== partnerId)
  .map((a) => a.preferredName || a.firstName || '')
  .filter(Boolean);

const assetRefs = useTemplateRefsList<InstanceType<typeof AssetForm>>();
const activeTab = ref<ApplicantId | undefined>(editableApplicants.length === 1 ? editableApplicants[0] : undefined);

const validateAssets = async () => {
  let isValid = true;
  const refCount = assetRefs.value.length;
  for (let i = 0; i < refCount; i++) {
    const formRef = assetRefs.value[i];
    try {
      isValid = (await formRef.update()) ?? false;
    } catch (err) {
      console.error(err);
      isValid = false;
    }
    if (!isValid) {
      activeTab.value = formRef.applicantId as ApplicantId;
      window.scrollTo(0, 0);
      break;
    }
  }

  const addressLines = Object.values(financials.value).flatMap((applicant) =>
    applicant.assets
      .filter((asset) => asset.type === 'property' && asset.address)
      .map((asset) => (asset as PropertyAsset).address.addressLine),
  );

  const hasDuplicateAddress = addressLines.some((addressLine, index) => addressLines.indexOf(addressLine) !== index);
  if (hasDuplicateAddress) {
    isValid = false;
    window.scrollTo(0, 0);
    toast({
      type: 'danger',
      title: 'Error',
      description: 'Please only enter a property asset once.',
    });
  }

  return isValid;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cardTitle = (item: any) => {
  let description = '';
  if (item.type === AssetType.PROPERTY) {
    description = item.address?.addressLine;
  } else if (item.type === AssetType.MOTOR_VEHICLES) {
    description = `${item.make ?? ''} ${item.model ?? ''}`.trim();
  } else if (item.type === AssetType.MACHINERY || item.type === AssetType.EQUIPMENT) {
    description = item.description ?? '';
  } else if (item.institution) {
    description = item.institution;
  } else if (item.model) {
    description = `${item.make} ${item.model}`;
  } else if (item.details) {
    description = item.details;
  }

  return `${item.type.charAt(0).toUpperCase() + item.type.slice(1).replace('_', ' ')}: ${description}`;
};

const cardDescription = (applicantId: ApplicantId) => {
  const assets = financials.value[applicantId].assets.map((item) => {
    return cardTitle(item);
  });
  const liabilities = financials.value[applicantId].liabilities.map((item) => {
    return cardTitle(item);
  });

  return [...assets, ...liabilities];
};

const saveAndContinue = async () => {
  if (await validateAssets()) {
    saveAndGoNext({
      financials: financials.value,
    });
  }
};
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        What
        <BxHighlight>assets</BxHighlight>
        and
        <BxHighlight>liabilities</BxHighlight>
        do you have?
      </h2>
    </div>
    <div v-if="loanApp.applicants.length > 1" class="my-6">
      <p v-if="partnerId">Please only include combined assets and liabilities once.</p>
      <p v-else-if="loanApp.actionType === 'createNew' && otherApplicantNames.length">
        Only include your assets and liabilities, we will request this information from
        {{ otherApplicantNames.join(', ') }} via email.
      </p>
    </div>

    <div class="my-6 grid gap-6">
      <div
        v-for="section in entitySections"
        :key="section.entity?.id ?? 'individuals'"
        :class="section.entity && 'rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-6'"
      >
        <h3 v-if="section.entity" class="mb-4 text-xl font-semibold text-bridgit-navy">
          {{ section.entity.name }}
        </h3>
        <p v-if="section.entity && !section.members.length" class="text-sm text-gray-500">
          No individual applicants are linked to this entity.
        </p>
        <div class="grid gap-4">
          <div v-for="member in section.members" :key="member.applicantId">
            <div v-if="!member.isDetailAnchor" class="text-sm text-gray-500">
              {{ getFullName(loanApplicants[member.applicantId]) }}
              <span v-if="member.roleLabel">· {{ member.roleLabel }}</span>
              <span v-if="member.partnerName" class="font-normal text-gray-500"
                >(Married to {{ member.partnerName }})</span
              >
              — assets entered under {{ member.anchorEntityName }}
            </div>
            <BxCard
              v-if="member.isDetailAnchor && canEditApplicantAssets(member.applicantId)"
              :class="member.applicantId !== activeTab && 'hidden'"
            >
              <h2 class="mb-1 text-xl font-medium">
                {{ getFullName(loanApplicants[member.applicantId]) }}
                <span v-if="member.roleLabel" class="text-base font-normal text-gray-500">
                  · {{ member.roleLabel }}
                </span>
                <span v-if="member.partnerName" class="text-base font-normal text-gray-500"
                  >(Married to {{ member.partnerName }})</span
                >
              </h2>
              <AssetForm
                :ref="assetRefs.set"
                v-model:assets="financials[member.applicantId].assets"
                v-model:liabilities="financials[member.applicantId].liabilities"
                :applicant-id="member.applicantId"
                :animate="member.applicantId === activeTab"
                :show-buttons="validApplicants.length > 1"
                @save="activeTab = undefined"
              />
            </BxCard>
            <BxCard
              v-if="member.isDetailAnchor"
              :class="[
                `${member.applicantId === activeTab && 'hidden'} asset-card`,
                !canEditApplicantAssets(member.applicantId) && 'opacity-60',
              ]"
            >
              <div class="flex items-center">
                <div class="card-details">
                  <div class="mb-1 font-medium">
                    {{ getFullName(loanApplicants[member.applicantId]) }}
                    <span v-if="member.roleLabel" class="font-normal text-gray-500">· {{ member.roleLabel }}</span>
                    <span v-if="member.partnerName" class="font-normal text-gray-500"
                      >(Married to {{ member.partnerName }})</span
                    >
                  </div>
                </div>
                <div class="asset-descriptions ml-6">
                  <div v-for="description in cardDescription(member.applicantId)" :key="description">
                    <span class="text-gray-400">{{ description }}</span>
                  </div>
                </div>

                <div v-if="canEditApplicantAssets(member.applicantId)" class="card-button">
                  <BxButton
                    :id="`applicant-${getApplicantIndex(member.applicantId)}-edit-or-add-assets`"
                    variant="secondary"
                    :disabled="
                      (activeTab && activeTab !== member.applicantId) ||
                      (loanApplicants[member.applicantId].type === 'primary' && isSecondaryDeclaration)
                    "
                    @click="activeTab = member.applicantId"
                  >
                    {{ financials[member.applicantId].assets.length ? 'Edit' : 'Add' }} assets
                  </BxButton>
                </div>
              </div>
            </BxCard>
            <BxCard
              v-if="member.isDetailAnchor"
              :class="[
                `${member.applicantId === activeTab && 'hidden'} asset-card-mobile`,
                !canEditApplicantAssets(member.applicantId) && 'opacity-60',
              ]"
            >
              <div class="items-left flex flex-col">
                <div class="card-details">
                  <div class="mb-1 font-medium">
                    {{ getFullName(loanApplicants[member.applicantId]) }}
                    <span v-if="member.roleLabel" class="font-normal text-gray-500">· {{ member.roleLabel }}</span>
                    <span v-if="member.partnerName" class="font-normal text-gray-500"
                      >(Married to {{ member.partnerName }})</span
                    >
                  </div>
                </div>
                <div class="asset-descriptions ml-6">
                  <div v-for="description in cardDescription(member.applicantId)" :key="description">
                    <span class="text-gray-400">{{ description }}</span>
                  </div>
                </div>
              </div>
              <div v-if="canEditApplicantAssets(member.applicantId)" class="card-button">
                <BxButton
                  variant="secondary"
                  :disabled="
                    (activeTab && activeTab !== member.applicantId) ||
                    (loanApplicants[member.applicantId].type === 'primary' && isSecondaryDeclaration)
                  "
                  @click="activeTab = member.applicantId"
                >
                  {{ financials[member.applicantId].assets.length ? 'Edit' : 'Add' }} assets
                </BxButton>
              </div>
            </BxCard>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6">
      <BxButton id="assets-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.asset-descriptions {
  @media (max-width: 767px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}

.asset-card {
  @media (max-width: 767px) {
    display: none;
  }
}

.asset-card-mobile {
  @media (min-width: 767px) {
    display: none;
  }
}

.card-button {
  flex-grow: 1;
  @media (min-width: 767px) {
    text-align: right;
  }
  @media (max-width: 767px) {
    text-align: center;
  }
  flex-basis: 200px;
}
</style>
